import { z } from 'zod';

import { ConversationTool } from './ConversationTool';

import {
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolExecutionContext,
} from 'js/src/agent/types';
import type { AspectRatio } from 'js/src/client';
import { VideoDraftResultRenderer } from 'js/src/components/video-planner/VideoDraftResultRenderer';
import { createAndPopulateVideoDraftFromVoiceoverAssetPlan } from 'js/src/data/video-planner';

// Not LLM-callable — invoked programmatically from the outline editor, so the fields carry
// no model-facing descriptions (matching the original hand-written schema). aspect_ratio stays
// a plain string (not the AspectRatio enum) to keep the emitted schema wire-identical.
export const createVideoFromVoiceoverAssetPlanSchema = z.object({
    audio_uuid: z.string(),
    asset_kind: z.enum(['audio', 'video']),
    title: z.string(),
    template_id: z.string(),
    aspect_ratio: z.string(),
    blueprint_id: z.number().optional(),
});
export type CreateVideoFromVoiceoverAssetPlanArgs = z.infer<
    typeof createVideoFromVoiceoverAssetPlanSchema
>;

/**
 * Programmatic tool (not LLM-callable) that builds the actual voiceover-driven
 * video once the user approves the outline produced by CreateVideoFromVoiceoverAssetTool.
 * Called from VideoPlanEditor's "Create video" button via executeTool.
 */
export class CreateVideoFromVoiceoverAssetPlanTool extends ConversationTool<
    typeof createVideoFromVoiceoverAssetPlanSchema
> {
    public static readonly toolName = 'create_video_from_voiceover_asset_plan';

    protected override readonly schema = createVideoFromVoiceoverAssetPlanSchema;
    protected override readonly toolName = CreateVideoFromVoiceoverAssetPlanTool.toolName;
    protected override readonly toolDescription =
        'Create a video from an approved voiceover plan (not LLM-callable — invoked programmatically from the outline editor).';

    public override isLLMCallable = false;
    public override canRunAsync = false;
    public override customRendererMode = CustomRendererMode.INLINE;
    override customRenderer = VideoDraftResultRenderer;

    public override getToolDisplayName(): string {
        return 'Creating video';
    }

    public get statusIcon(): string {
        return 'movie';
    }

    public async execute(
        args: CreateVideoFromVoiceoverAssetPlanArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const videoDraftId = context.toolCallId;

        try {
            const result = await this._dispatch(
                createAndPopulateVideoDraftFromVoiceoverAssetPlan({
                    videoDraftId,
                    audioUuid: args.audio_uuid,
                    assetKind: args.asset_kind,
                    title: args.title,
                    templateId: args.template_id,
                    aspectRatio: args.aspect_ratio as AspectRatio,
                    blueprintId: args.blueprint_id,
                    creationToolName: CreateVideoFromVoiceoverAssetPlanTool.toolName,
                }),
            );

            if (!result.success) {
                return {
                    result: result.errorMessage ?? 'Video draft generation failed.',
                    isSuccess: false,
                };
            }

            analytics.track('Sketch: Video Created From Voiceover', {
                asset_kind: args.asset_kind,
                template_id: args.template_id,
                aspect_ratio: args.aspect_ratio,
                blueprint_id: args.blueprint_id,
                success: true,
            });

            return {
                result: JSON.stringify({
                    videoDraftId,
                    success:
                        'Video successfully created from voiceover. Video is embedded above. The user can click it to view, edit and download.',
                }),
                isSuccess: true,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

            analytics.track('Sketch: Video Create From Voiceover Error', {
                error_message: errorMessage,
            });

            return {
                result: `Error creating video from voiceover: ${errorMessage}`,
                isSuccess: false,
            };
        }
    }
}
